import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Button, Box, Text } from '@rsos/base-components';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import type {
  ConfigureIDPProps,
  IDPConfigurationValues,
} from '../../types/sso';

const defaultInitialValues: IDPConfigurationValues = {
  domain: '',
};

const validationSchema = Yup.object({
  domain: Yup.string()
    .required('Please enter a valid domain')
    .matches(
      // There's no need to be too strict with validation since we can't
      // account for all possible TLDS and host combinations. Instead, we'll
      // just check that there's obvious domain structure to guide the user.
      // For example,
      // foo.com, foo.co.uk, foo.com.au should pass.
      // foo, foo., foo.com. should fail.
      /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/,
      'Please enter a valid domain',
    ),
});

const ConfigureIDP: React.FC<ConfigureIDPProps> = ({
  initialValues = {},
  onSubmit = (values, actions) => {
    // eslint-disable-next-line no-console -- defensive guard: should be unreachable
    console.error(
      'ConfigureIDP: onSubmit callback not provided. Form submission will not work.',
    );
    actions.setSubmitting(false);
  },
}) => {
  const initValues: IDPConfigurationValues = {
    ...defaultInitialValues,
    ...initialValues,
  };

  return (
    <>
      <Text size="header" fontWeight="semibold" color="primaryText" mb={10}>
        Configure your Identity Provider
      </Text>
      <Text size="normal" color="primaryText" mb={10}>
        Your Identity Provider (IdP) is the service that authenticates your
        users. Once setup you'll be able to configure SSO settings below. Please
        have an authorized system administrator configure or update these
        settings
      </Text>
      <Box mt={10}>
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            onSubmit(values, {
              setSubmitting: (isSubmitting: boolean) => {
                setSubmitting(isSubmitting);
              },
            });
          }}
        >
          {({
            dirty,
            errors,
            handleBlur,
            handleChange,
            isSubmitting,
            isValid,
            setFieldValue,
            touched,
            values,
          }) => (
            <Form>
              <Box width="100%">
                <Box>
                  <InputText
                    id="domain"
                    name="domain"
                    label="Enter the domain used by your organization for SSO (it should match the domain of your sign-in email)"
                    placeholder="example.com"
                    value={values.domain}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    shouldErrorTakeSpace
                    error={
                      errors.domain && touched.domain ? errors.domain : null
                    }
                  />
                </Box>
                <Box mt={20}>
                  <Button
                    type="submit"
                    btnType="primary"
                    disabled={
                      initValues.domain
                        ? !isValid || isSubmitting
                        : !dirty || !isValid || isSubmitting
                    }
                    isLoadingProp={isSubmitting}
                  >
                    Setup your Identity Provider
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};

export default ConfigureIDP;
